import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { FaqCategory, FaqCategoryPayload } from '../types/faq';

const BASE = '/admin/v1/faq-categories';

/** be-catalog 계약: admin write 는 Idempotency-Key 헤더 필요. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

interface ListEnvelope {
  success: boolean;
  data?: FaqCategory[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: FaqCategory | null;
}

export const faqCategoriesQueryKey = ['admin', 'faq-categories'] as const;

const fetchFaqCategories = async (): Promise<FaqCategory[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`faq categories fetch failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

export const useFaqCategoriesQuery = () =>
  useQuery<FaqCategory[]>({
    queryKey: faqCategoriesQueryKey,
    queryFn: fetchFaqCategories,
    staleTime: 60 * 1000,
  });

export const useFaqCategoryMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: faqCategoriesQueryKey });

  const create = useMutation({
    mutationFn: async (payload: FaqCategoryPayload): Promise<FaqCategory> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: writeHeaders('POST', BASE, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`faq category create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      categoryId,
      payload,
    }: {
      categoryId: number;
      payload: FaqCategoryPayload;
    }): Promise<FaqCategory> => {
      const path = `${BASE}/${categoryId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`faq category update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (categoryId: number): Promise<void> => {
      const path = `${BASE}/${categoryId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: writeHeaders('DELETE', path, ''),
      });
      if (!res.ok) {
        if (res.status === 409) throw new Error('CONFLICT');
        throw new Error(`faq category delete failed: ${res.status}`);
      }
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
